import Link from '@/components/Link';
import type {
  Group,
  GroupBackgroundStyleVariant,
  GroupHeaderSettings,
  GroupSettings,
} from '@gik/core/models/gik/Group';
import routes from '@gik/core/routes';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useGroupDefaults } from '@gik/inkind-page/hooks/useGroupDefaults';
import { HTMLParser } from '@gik/ui/HTMLParser';
import React from 'react';
import { InkindGroupWidgetGrid } from './InkindGroupWidgetGrid';

export function useGroupVisibility(group: Group): boolean {
  return React.useMemo(
    () =>
      group?.inkindGroupSettings?.membershipFeatures?.inkindPageWidget &&
      group?.inkindGroupSettings?.inkindPageWidget?.visible &&
      (group?.inkindGroupSettings?.inkindPageWidget?.links?.length > 0 ||
        group?.inkindGroupSettings?.inkindPageWidget?.groupDescriptionVisible),
    [group]
  );
}

export type InkindGroupWidgetProps = {
  group?: Group;
  muted?: boolean;
  disabledWarning?: boolean;
  groupSettings?: GroupHeaderSettings;
} & UIComponent;

function InkindGroupWidgetComp({
  group: _group,
  groupSettings,
  muted,
  disabledWarning,
  className,
  ...otherProps
}: React.PropsWithChildren<InkindGroupWidgetProps>): React.ReactElement {
  const bem = useBemCN('inkind-group-widget');
  const group = useGroupDefaults(_group);

  const settings: Partial<GroupSettings> = groupSettings || group?.inkindGroupSettings || {};
  if (!settings) return null;

  const styleVariant: GroupBackgroundStyleVariant = settings.styleVariant;

  return (
    <div
      {...bem(
        null,
        [
          {
            ['has-gradient']: styleVariant === 'gradient',
          },
          { muted },
        ],
        [className]
      )}
      style={{
        // @ts-ignore
        ['--background']: (styleVariant === 'solid' || !styleVariant) && settings.brandColor,
        ['--gradient-color-1']: settings.gradientColor1,
        ['--gradient-color-2']: settings.gradientColor2,
        ['--background-image']: styleVariant === 'image' && `url("${settings.bannerImageUrl}")`,
      }}
      {...otherProps}
    >
      <header {...bem('header')}>
        <Link href={`${routes.groups}/${group?.id}`} scroll>
          <img src={group?.inkindGroupSettings?.logoImageUrl} alt="Link thumbnail" {...bem('logo')} />
        </Link>
      </header>
      <main {...bem('main')}>
        {disabledWarning &&
          !group?.inkindGroupSettings?.inkindPageWidget?.links?.length &&
          !group?.inkindGroupSettings?.inkindPageWidget?.groupDescriptionVisible && (
            <p {...bem('disabled-warning')}>
              <span className="tw-font-bold">WIDGET DISABLED.</span> At least one link or the group description must be
              included to diplay the widget.
            </p>
          )}
        {group?.inkindGroupSettings?.inkindPageWidget?.groupDescriptionVisible && (
          <div {...bem('description')}>
            <HTMLParser
              rawHtml={
                group?.inkindGroupSettings?.useCustomDescription
                  ? group?.inkindGroupSettings?.customDescription
                  : group?.inkindGroupSettings?.description
              }
              {...bem('description')}
            />
          </div>
        )}

        {group?.inkindGroupSettings?.inkindPageWidget?.links?.length > 0 && <InkindGroupWidgetGrid group={group} />}
      </main>
    </div>
  );
}

export const InkindGroupWidget = withComponentErrorBoundary(InkindGroupWidgetComp);
